import { keyframes } from 'styled-components'
import tw, { styled } from 'twin.macro'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import type { PropsWithChildren } from 'react'

import type { TooltipProps } from './types'

const scaleIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }

  100%: {
    opacity: 1;
    transform: scale(1);
  }
`

const scaleOut = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }

  100% {
    opacity: 0;
    transform: scale(0.8);
  }
`

const StyledContent = styled(TooltipPrimitive.Content)`
  ${tw`
    p-2.5
    rounded
    bg-inverted-emphasis
    font-brand
    leading-tight
    text-left
    text-xs-brand
    text-inverted-muted
    `}

  // this is to guarantee that the whole tooltip content is readable
  // in cases that a very long word/email is used as the content
  ${tw`break-words`}
  max-width: 250px;

  transform-origin: var(--radix-tooltip-content-transform-origin);
  animation: ${scaleIn} 0.15s ease-out;

  &[data-state='closed'] {
    animation: ${scaleOut} 0.15s ease-out;
  }
`

const StyledArrow = styled(TooltipPrimitive.Arrow)`
  ${tw`
    text-default-emphasis
    fill-current
  `};
`

const Tooltip = function Tooltip({
  children,
  content,
  // tooltip root
  open,
  defaultOpen,
  onOpenChange,
  delayDuration,
  skipDelayDuration,
  // tooltip content
  ...contentProps
}: PropsWithChildren<TooltipProps>) {
  return (
    <TooltipPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      delayDuration={delayDuration ?? 100}
      skipDelayDuration={skipDelayDuration}
    >
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <StyledContent alignOffset={-4} sideOffset={4} {...contentProps}>
        {content}
        <StyledArrow offset={9} width={14} height={7} />
      </StyledContent>
    </TooltipPrimitive.Root>
  )
}

export default Tooltip
